<template>
    <div class="apply-theme-container">
        <el-form>
            <div class="customize-card">
                <h3 class="card-title">1. Select a Theme</h3>
                <el-form-item class="mb-0">
                    <p>
                        <el-button type="primary" @click="selectTheme()">
                            Select a Theme
                        </el-button>
                    </p>
                    <p class="mt-0">
                        You may change your theme at any time by viewing the
                        themes and clicking on a new theme.
                    </p>
                    <form-feedback :form="form" field="theme_id" />
                    <h4 v-if="selectedTheme" class="selected-theme">
                        Selected Theme: {{ selectedTheme.name }}
                    </h4>
                    <!-- Development mode message -->
                    <div
                        v-if="isDevelopment && !hasAvailableThemes"
                        class="dev-message"
                    >
                        <el-alert
                            title="Development Mode"
                            type="info"
                            description="No themes available for this month/year. You can still generate a proof using a blank template."
                            show-icon
                            :closable="false"
                        >
                        </el-alert>
                    </div>
                </el-form-item>
            </div>

            <div class="customize-card" v-if="selectedThemeCategory == null">
                <h3 class="card-title">Customize Title Colors</h3>
                <p class="el-form-item__content">
                    Customize the appearance of your calendar title by selecting
                    colors that match your theme.
                </p>
                <div class="color-selection">
                    <div class="color-option">
                        <h4>Title Color</h4>
                        <custom-color-picker
                            v-model="form.title_color"
                            :predefine="availableColors"
                        >
                        </custom-color-picker>
                    </div>
                    <div class="color-option">
                        <h4>Title Outline Color</h4>
                        <custom-color-picker
                            v-model="form.title_outline_color"
                            :predefine="availableColors"
                        >
                        </custom-color-picker>
                    </div>
                </div>
            </div>

            <div
                class="customize-card"
                v-if="selectedTheme || (isDevelopment && !hasAvailableThemes)"
            >
                <h3 class="card-title">2. Preview & Proof</h3>
                <p class="el-form-item__content">
                    <span v-if="selectedTheme">
                        Click on the thumbnail below to view the full theme
                        image,<br />
                        then use the proof button to see how your calendar will
                        look.
                    </span>
                    <span v-else-if="isDevelopment">
                        No theme selected. The proof will show your calendar
                        with a blank template.
                    </span>
                </p>
                <div class="preview-section">
                    <div class="thumbnail-container" v-if="selectedTheme">
                        <LightBox
                            :images="images"
                            :show-light-box="false"
                            ref="lightbox"
                        />
                        <a
                            href="#"
                            @click.prevent="$refs.lightbox.showImage(0)"
                            class="theme-preview"
                        >
                            <img
                                v-if="
                                    selectedThemeFile &&
                                    selectedThemeFile.preview &&
                                    selectedThemeFile.preview.url
                                "
                                :src="selectedThemeFile.preview.url"
                                alt="Theme Preview"
                            />
                            <div v-else class="no-preview">
                                Preview not available
                            </div>
                        </a>
                    </div>
                    <div class="proof-section">
                        <h3 class="card-title">View Your Proof</h3>
                        <p class="el-form-item__content">
                            Click the button below to see how your calendar will
                            look with the current settings.
                        </p>
                        <el-button
                            type="primary"
                            icon="mdi mdi-file-document-outline"
                            :loading="loading_proof"
                            @click="viewProof()"
                            :disabled="!canViewProof"
                        >
                            Save and View Proof
                        </el-button>
                    </div>
                </div>
            </div>

            <el-dialog
                :close-on-click-modal="false"
                title="Choose Theme"
                :visible.sync="dialogVisible"
                width="80%"
                max-width="1200px"
                class="theme-dialog"
            >
                <div class="theme-grid">
                    <div
                        v-for="theme in themes"
                        :key="theme.id"
                        class="theme-item"
                        :class="{
                            'theme-selected': form.theme_id === theme.id
                        }"
                    >
                        <a
                            href="#"
                            @click.prevent="chooseTheme(theme)"
                            class="theme-link"
                        >
                            <img
                                v-if="
                                    getThemeFile(theme) &&
                                    getThemeFile(theme).preview
                                "
                                :src="getThemeFile(theme).preview.url"
                                :alt="theme.name"
                                class="theme-image"
                            />
                            <div v-else class="no-preview">
                                Preview not available
                            </div>
                            <div class="theme-name">{{ theme.name }}</div>
                        </a>
                    </div>
                </div>
            </el-dialog>

            <el-dialog
                title="Preview Proof"
                fullscreen
                :visible.sync="viewing_proof"
                :show-close="false"
            >
                <template slot="title">
                    <div class="proof-dialog-title">
                        Calendar Proof Preview
                        <span
                            class="close-button"
                            @click="viewing_proof = false"
                            style="
                                cursor: pointer;
                                font-weight: bold;
                                margin-left: auto;
                            "
                            >Close ×</span
                        >
                    </div>
                </template>
                <proof
                    v-if="viewing_proof"
                    :calendar="calendar"
                    :theme="selectedTheme"
                />
            </el-dialog>
        </el-form>
    </div>
</template>

<script>
import * as ThemeCategories from "@/constants/theme-categories";
import theme_mixin from "@/mixins/theme";
import _ from "lodash";
import LightBox from "vue-image-lightbox";
import { mapState } from "vuex";
import Proof from "./CalendarProof";
import Environment from "@/utils/Environment";

export default {
    //selectedThemeCategory from theme_mixin
    mixins: [theme_mixin],
    components: {
        LightBox,
        Proof
    },
    props: {
        form: {
            required: true,
            type: Object
        },
        calendar: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            dialogVisible: false,
            themes: [],
            viewing_proof: false,
            loading_proof: false
        };
    },
    computed: {
        current_community() {
            return this.$parent.current_community;
        },
        availableColors() {
            return this.$store.state.config.settings.calendar.title_colors;
        },
        ...mapState({
            predefinedColors: (state) =>
                state.config.settings.calendar.title_colors
        }),
        selectedTheme() {
            return this.themes.find((item) => {
                return item.id === this.form.theme_id;
            });
        },
        selectedThemeFile() {
            if (!this.selectedTheme) {
                return null;
            }
            let category = this.selectedTheme.categories.data.find(
                (item) => item.name === this.categoryName && item.file.preview
            );

            if (category) {
                return category.file;
            }
            return this.selectedTheme.categories.data.find(
                (item) => !!item.file.preview
            ).file;
        },
        images() {
            if (!this.selectedTheme) {
                return [];
            }
            return [
                {
                    thumb: _.get(this.selectedThemeFile, "preview.url"),
                    src: _.get(this.selectedThemeFile, "preview.url"),
                    caption: this.selectedTheme.name
                }
            ];
        },
        themeCategory() {
            if (!this.selectedTheme) {
                return null;
            }
            return this.selectedTheme.categories.data.find(
                (item) => item.name === this.categoryName
            );
        },
        categoryName() {
            if (!this.$store.state.current_community) {
                // corwin employee editing the calendar
                return this.calendar.theme_category;
            }
            const hasLogo =
                this.$store.state.current_community &&
                this.$store.state.current_community.has_logo &&
                !this.$store.state.current_community.no_calendar_logo &&
                !this.calendar.remove_logo;

            // Always use the explicit logic rather than relying on mixed sources
            return hasLogo
                ? ThemeCategories.WITH_LOGO
                : ThemeCategories.NO_LOGO;
        },
        // Add computed property to check if we're in development
        isDevelopment() {
            return Environment.isDevelopment();
        },
        // Add computed property to check if themes are available
        hasAvailableThemes() {
            return this.themes && this.themes.length > 0;
        },
        // Allow proof viewing in development even without theme
        canViewProof() {
            return (
                this.selectedTheme ||
                (this.isDevelopment && !this.hasAvailableThemes)
            );
        }
    },
    mounted() {
        this.getThemes();
    },
    methods: {
        selectTheme() {
            this.dialogVisible = true;
        },
        chooseTheme(theme) {
            this.loading = true;

            axios
                .post(`/web-api/calendar/${this.calendar.id}/apply-theme`, {
                    theme_id: theme.id
                })
                .then((response) => {
                    // Save the existing community object to preserve all its properties
                    const existingCommunity = this.calendar.community;

                    // Update the local calendar object with the response
                    Object.assign(this.calendar, response.data.calendar);

                    // Restore the original community object if it's missing in the response
                    if (
                        existingCommunity &&
                        (!this.calendar.community ||
                            !this.calendar.community.locations)
                    ) {
                        this.calendar.community = existingCommunity;
                    }

                    this.form.theme_id = theme.id;
                    this.form.title_color = response.data.calendar.title_color;
                    this.form.title_outline_color =
                        response.data.calendar.title_outline_color;
                    this.dialogVisible = false;
                })
                .catch((error) => {
                    console.error("Error applying theme:", error);
                    this.$message.error("Failed to apply theme");
                })
                .finally(() => {
                    this.loading = false;
                    this.dialogVisible = false;
                });
        },
        getThemes() {
            window.axios
                .get(`/web-api/theme?include=categories.file.preview`, {
                    params: {
                        month: this.form.month,
                        year: this.form.year,
                        for_current_community: 1,
                        community_location_id: this.form.community_location_id
                    }
                })
                .then((response) => {
                    this.themes = response.data.data;

                    // In development, show a message if no themes are available
                    if (
                        Environment.shouldShowDevMessages() &&
                        this.themes.length === 0
                    ) {
                        console.log(
                            "Development mode: No themes available for this month/year. Proof will use blank template."
                        );
                    }
                });
        },
        viewProof() {
            this.loading_proof = true;
            this.$parent
                .saveTheme()
                .then((response) => {
                    _.extend(
                        this.calendar,
                        _.pick(response, ["theme_id", "current_step"])
                    );
                    // Update parent snapshot to clear unsaved changes warning
                    if (typeof this.$parent.initOldData === "function") {
                        this.$parent.initOldData();
                    }
                    this.viewing_proof = true;
                })
                .finally(() => (this.loading_proof = false));
        },
        getThemeFile(theme) {
            if (!theme) {
                return null;
            }
            let category = theme.categories.data.find(
                (item) => item.name === this.categoryName && item.file.preview
            );
            if (category) {
                return category.file;
            }
            return theme.categories.data.find((item) => !!item.file.preview);
        },
        getThemeCategoryTitleColor(theme) {
            return theme.categories.data.find(
                (item) => item.name === this.categoryName
            ).title_color;
        }
    },
    watch: {
        "form.month"() {
            this.getThemes();
            this.form.theme_id = null;
        }
    }
};
</script>

<style lang="scss" scoped>
@use "../../../../../assets/scss/variables" as vars;

.apply-theme-container {
    padding-top: 20px;
}

.customize-card {
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 20px;
    border: 1px solid #eaeaea;
    margin-bottom: 20px;
    max-width: 1000px;
}

.card-title {
    margin-bottom: 20px;
    padding-left: 5px;
    color: #333;
    font-size: 18px;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 10px;
}

.selected-theme {
    margin-top: 15px;
    font-weight: 600;
    color: vars.$corwin-blue-green;
}

.color-selection {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    margin-top: 20px;
}

.color-option {
    flex: 1;
    min-width: 200px;
    
    h4 {
        margin-top: 0;
        margin-bottom: 10px;
        font-size: 16px;
    }
}

.preview-section {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    margin-top: 20px;
    align-items: flex-start;
}

.thumbnail-container {
    flex: 1;
    min-width: 300px;
}

.proof-section {
    flex: 1;
    min-width: 300px;
    
    h4 {
        margin-top: 0;
        margin-bottom: 10px;
        font-size: 16px;
    }
}

.theme-preview {
    display: block;
    overflow: hidden;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
        border-color: vars.$corwin-blue-green;
    }
    
    img {
        width: 100%;
        height: auto;
        display: block;
    }
}

.no-preview {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f0f0f0;
    color: #909399;
    font-style: italic;
}

.theme-dialog {
    .el-dialog__body {
        padding-top: 10px;
    }
}

.theme-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
}

.theme-item {
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &.theme-selected {
        box-shadow: 0 0 0 3px vars.$corwin-blue-green;
    }
}

.theme-link {
    display: block;
    color: inherit;
    text-decoration: none;
}

.theme-image {
    width: 100%;
    height: 180px;
    object-fit: cover;
    display: block;
}

.theme-name {
    padding: 10px;
    text-align: center;
    font-weight: 500;
    background-color: #f5f7fa;
    border-top: 1px solid #ebeef5;
}

.proof-dialog-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    color: vars.$corwin-blue-green;

    .close-button {
        margin-left: auto;
        font-size: 20px;
        color: vars.$corwin-blue-green
    }
}

.dev-message {
    margin-top: 15px;
    
    .el-alert {
        border-radius: 4px;
    }
}

@media (max-width: 768px) {
    .color-selection,
    .preview-section {
        flex-direction: column;
        gap: 20px;
    }
    
    .color-option,
    .thumbnail-container,
    .proof-section {
        width: 100%;
    }
    
    .theme-grid {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
}
</style>
